import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, Link2, Sparkles, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * Activation Links panel — Feb 2026.
 *
 * Surfaces every tracking URL ever minted for this SMB Lead so the
 * sales team can copy-paste the activation link without scrolling
 * WhatsApp. Also lets them mint a fresh link on-demand (e.g. when the
 * previous WA send failed).
 *
 * API contract:
 *   GET  /api/smb/leads/{id}/activation-links → { data: [ { slug, url,
 *          message_number, message_label, created_at,
 *          delivery_status, delivery_channel, error } ] }
 *   POST /api/smb/leads/{id}/mint-activation-link → { data: { url } }
 */
export const ActivationLinksPanel = ({ leadId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);

  const fetch = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await api.get(`/smb/leads/${leadId}/activation-links`);
      setRows(res.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load activation links');
    } finally {
      setLoading(false);
    }
  }, [leadId]);
  useEffect(() => { fetch(); }, [fetch]);

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');   // decision (i) — short confirmation
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const mintAndCopy = async () => {
    if (minting) return;
    setMinting(true);
    try {
      const res = await api.post(`/smb/leads/${leadId}/mint-activation-link`);
      const url = res.data?.data?.url;
      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
        fetch();   // refresh so the newly-minted row appears in the list
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mint link');
    } finally {
      setMinting(false);
    }
  };

  const statusBadge = (s) => {
    if (s === 'sent')     return <Badge className="bg-emerald-500/10 text-emerald-500 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Delivered</Badge>;
    if (s === 'failed')   return <Badge className="bg-red-500/10 text-red-500 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />Delivery failed</Badge>;
    if (!s)               return <Badge variant="outline" className="text-[10px]">Not yet sent</Badge>;
    return <Badge variant="outline" className="text-[10px]">{s}</Badge>;
  };

  const kindBadge = (kind) => {
    if (kind === 'activation_payment') {
      return <Badge className="bg-orange-500/15 text-orange-500 border-orange-500/40 text-[10px]">Payment ₹999</Badge>;
    }
    return <Badge variant="outline" className="text-[10px]">Preview</Badge>;
  };

  // "Latest" priority: the activation payment link ALWAYS wins (that's
  // what sales actually needs to hand a customer). Fall back to the
  // newest preview short-link only when no activation token exists.
  const activationRow = rows.find((r) => r.kind === 'activation_payment');
  const latest = activationRow || rows[0];

  return (
    <Card data-testid="activation-links-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" />
            Activation Links
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={mintAndCopy}
            disabled={minting}
            data-testid="mint-activation-link-btn"
            title="Mint a fresh activation link and copy to clipboard (no message will be sent)"
          >
            {minting
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Sparkles className="h-4 w-4 mr-1" />}
            Generate & copy
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {latest && (
          <div
            className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2"
            data-testid="activation-latest-link"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span>Latest link ({latest.message_label})</span>
                {kindBadge(latest.kind)}
              </div>
              {statusBadge(latest.delivery_status)}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] font-mono truncate rounded bg-muted/40 px-2 py-1">
                {latest.url}
              </code>
              <Button
                size="sm"
                onClick={() => copy(latest.url)}
                data-testid="copy-latest-link-btn"
              >
                <Copy className="h-4 w-4 mr-1" /> Copy latest
              </Button>
            </div>
            {latest.kind !== 'activation_payment' && (
              <div className="text-[10px] text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                No activation payment link yet — click <strong>Generate &amp; copy</strong> to mint one.
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-3">
            No activation links yet. Click <strong>Generate &amp; copy</strong> above to create one.
          </div>
        ) : rows.length > 1 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground pb-1">
              History
            </div>
            {rows.filter((r) => r.slug !== latest?.slug).map((r) => (
              <div
                key={r.slug}
                className="flex items-center gap-2 py-1.5 border-t border-border/40 first:border-t-0"
                data-testid={`activation-link-row-${r.slug}`}
              >
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {r.message_label}
                </Badge>
                {kindBadge(r.kind)}
                <code className="flex-1 text-[11px] font-mono truncate text-muted-foreground">
                  {r.url}
                </code>
                {statusBadge(r.delivery_status)}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 shrink-0"
                  onClick={() => copy(r.url)}
                  data-testid={`copy-link-${r.slug}`}
                  title="Copy this link"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivationLinksPanel;
